import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, ShieldCheck, ShieldAlert, Loader2, ScanSearch, FileText,
  CheckCircle2, XCircle, AlertTriangle, History, RefreshCw, KeyRound,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const pct = (v) => `${(v * 100).toFixed(1)}%`;

const verdictStyle = (cls) => {
  const c = (cls || "").toLowerCase();
  if (c.includes("human")) return "bg-[#e9f1fd] text-[#0050c0] border-[#c8dcf7]";
  if (c.includes("ai")) return "bg-[#fef2e5] text-[#c25a00] border-[#f8ddc0]";
  return "bg-amber-50 text-amber-700 border-amber-200";
};

const VerdictBadge = ({ cls, confidence }) => (
  <span
    data-testid="qa-verdict-badge"
    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-nunito font-extrabold uppercase tracking-[0.1em] ${verdictStyle(cls)}`}
  >
    {(cls || "").toLowerCase().includes("human") ? <CheckCircle2 size={12} /> :
      (cls || "").toLowerCase().includes("ai") ? <XCircle size={12} /> : <AlertTriangle size={12} />}
    {cls || "unknown"}{confidence ? ` · ${confidence} confidence` : ""}
  </span>
);

const ProbBar = ({ label, value, color }) => (
  <div className="space-y-1">
    <div className="flex justify-between text-[11px] font-nunito font-extrabold uppercase tracking-[0.1em] text-[#6e6e6e]">
      <span>{label}</span>
      <span>{pct(value)}</span>
    </div>
    <div className="h-2 rounded-full bg-[#f0f0f0] overflow-hidden">
      <div className={`h-full rounded-full ${color}`} style={{ width: pct(value) }} />
    </div>
  </div>
);

export default function ContentQAPage() {
  const [status, setStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [history, setHistory] = useState([]);
  const [scoring, setScoring] = useState(null); // slug being scored
  const [result, setResult] = useState(null);
  const [pasted, setPasted] = useState("");

  const refresh = useCallback(async () => {
    try {
      const [s, c, h] = await Promise.all([
        axios.get(`${API}/ai-detect/status`),
        axios.get(`${API}/ai-detect/content`),
        axios.get(`${API}/ai-detect/history`),
      ]);
      setStatus(s.data);
      setItems(c.data.items || []);
      setHistory(h.data || []);
    } catch (e) {
      toast.error("Could not reach the engine backend.");
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const score = async (payload, label) => {
    setScoring(label);
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/ai-detect/score`, payload);
      setResult(data);
      toast.success(`Scored: ${data.record.label}`);
      axios.get(`${API}/ai-detect/history`).then((h) => setHistory(h.data || []));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scoring failed.");
    } finally {
      setScoring(null);
    }
  };

  return (
    <div data-testid="content-qa-page" className="min-h-screen bg-[#f7f7f7]">
      <header className="bg-[#0b0b0c] text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ScanSearch size={22} className="text-[#0050c0]" />
            <div>
              <h1 className="font-nunito text-lg font-extrabold">Content QA — AI Detection</h1>
              <p className="text-[12px] text-[#9a9a9a]">GPTZero scoring for every article before (and after) it ships</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/deploy" data-testid="qa-back-to-deploy" className="inline-flex items-center gap-1.5 text-[12px] font-nunito font-extrabold uppercase tracking-[0.1em] text-[#dedede] hover:text-white">
              <ArrowLeft size={14} /> Deploy Engine
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
        {/* Status */}
        <section data-testid="qa-status-card" className="rounded-[12px] border border-[#ececec] bg-white p-5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            {status?.configured
              ? <ShieldCheck size={20} className="text-[#0050c0]" />
              : <KeyRound size={20} className="text-amber-500" />}
            <div>
              <p className="font-nunito text-sm font-extrabold text-[#383838]">
                {status?.configured ? "GPTZero connected" : "Awaiting GPTZero API key"}
              </p>
              <p className="text-[12px] text-[#8a8a8a]">
                {status?.configured
                  ? status.provider
                  : "Everything is wired — paste GPTZERO_API_KEY into backend/.env and scoring goes live instantly."}
              </p>
            </div>
          </div>
          <button
            data-testid="qa-refresh-button"
            onClick={refresh}
            className="inline-flex items-center gap-1.5 rounded-[8px] border border-[#d9d9d9] px-3.5 py-2 text-[11px] font-nunito font-extrabold uppercase tracking-[0.1em] text-[#383838] hover:border-[#0050c0] hover:text-[#0050c0] transition-colors duration-150"
          >
            <RefreshCw size={13} /> Refresh
          </button>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Content list */}
          <section data-testid="qa-content-list" className="rounded-[12px] border border-[#ececec] bg-white">
            <div className="px-5 py-4 border-b border-[#f1f1f1] flex items-center gap-2">
              <FileText size={15} className="text-[#0050c0]" />
              <h2 className="font-nunito text-sm font-extrabold text-[#383838]">Engine articles ({items.length})</h2>
            </div>
            <div className="max-h-[420px] overflow-y-auto divide-y divide-[#f7f7f7]">
              {items.map((it) => (
                <div key={it.slug} className="px-5 py-3 flex items-center justify-between gap-3 hover:bg-[#fafafa]">
                  <div className="min-w-0">
                    <p className="font-nunito text-[13px] font-extrabold text-[#383838] truncate">{it.title}</p>
                    <p className="text-[11px] text-[#8a8a8a]">{it.words} words{it.backdated ? ` · dated ${it.backdated.slice(0, 10)}` : ""}</p>
                  </div>
                  <button
                    data-testid={`qa-score-button-${it.slug}`}
                    disabled={!!scoring || !status?.configured}
                    onClick={() => score({ slug: it.slug }, it.slug)}
                    className="shrink-0 inline-flex items-center gap-1.5 rounded-[8px] bg-[#f07000] px-3.5 py-2 text-[11px] font-nunito font-extrabold uppercase tracking-[0.1em] text-white hover:bg-[#d96400] disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
                  >
                    {scoring === it.slug ? <Loader2 size={13} className="animate-spin" /> : <ScanSearch size={13} />}
                    Score
                  </button>
                </div>
              ))}
            </div>
          </section>

          {/* Paste text + result */}
          <div className="space-y-6">
            <section data-testid="qa-paste-card" className="rounded-[12px] border border-[#ececec] bg-white p-5 space-y-3">
              <h2 className="font-nunito text-sm font-extrabold text-[#383838]">Score pasted text</h2>
              <textarea
                data-testid="qa-paste-textarea"
                value={pasted}
                onChange={(e) => setPasted(e.target.value)}
                rows={5}
                placeholder="Paste page copy or a draft article (250+ characters)…"
                className="w-full rounded-[8px] border border-[#e2e2e2] p-3 text-[13px] text-[#383838] focus:outline-none focus:border-[#0050c0]"
              />
              <button
                data-testid="qa-score-pasted-button"
                disabled={!!scoring || !status?.configured || pasted.trim().length < 250}
                onClick={() => score({ text: pasted }, "pasted")}
                className="inline-flex items-center gap-1.5 rounded-[8px] bg-[#0b0b0c] px-4 py-2.5 text-[11px] font-nunito font-extrabold uppercase tracking-[0.1em] text-white hover:bg-[#222] disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
              >
                {scoring === "pasted" ? <Loader2 size={13} className="animate-spin" /> : <ScanSearch size={13} />}
                Score text ({pasted.trim().length} chars)
              </button>
            </section>

            {/* Result */}
            {result && (
              <section data-testid="qa-result-card" className="rounded-[12px] border border-[#ececec] bg-white p-5 space-y-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <h2 className="font-nunito text-sm font-extrabold text-[#383838]">{result.record.label}</h2>
                    <p className="text-[11px] text-[#8a8a8a]">{result.record.words} words · {result.record.sentence_count} sentences</p>
                  </div>
                  <VerdictBadge cls={result.result.predicted_class} confidence={result.result.confidence_category} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <ProbBar label="Human" value={result.result.class_probabilities.human} color="bg-[#0050c0]" />
                  <ProbBar label="Mixed" value={result.result.class_probabilities.mixed} color="bg-amber-400" />
                  <ProbBar label="AI" value={result.result.class_probabilities.ai} color="bg-[#f07000]" />
                </div>
                {result.result.result_message && (
                  <p className="text-[12px] text-[#6e6e6e] italic">{result.result.result_message}</p>
                )}
                <div>
                  <h3 className="font-nunito text-[12px] font-extrabold uppercase tracking-[0.1em] text-[#6e6e6e] mb-2">
                    Flagged sentences ({result.result.flagged_sentences.length})
                  </h3>
                  {result.result.flagged_sentences.length === 0 ? (
                    <p data-testid="qa-no-flags" className="text-[13px] text-[#0050c0] flex items-center gap-1.5">
                      <ShieldCheck size={14} /> No sentences flagged as AI-like.
                    </p>
                  ) : (
                    <div data-testid="qa-flagged-list" className="space-y-2 max-h-[260px] overflow-y-auto">
                      {result.result.flagged_sentences.map((s, i) => (
                        <div key={i} className="rounded-[8px] bg-amber-50 border border-amber-200 px-3 py-2">
                          <p className="text-[13px] text-[#383838]">{s.text}</p>
                          <p className="text-[10.5px] text-amber-700 font-nunito font-extrabold mt-1">AI prob: {pct(s.generated_prob)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </section>
            )}
          </div>
        </div>

        {/* History */}
        <section data-testid="qa-history-card" className="rounded-[12px] border border-[#ececec] bg-white">
          <div className="px-5 py-4 border-b border-[#f1f1f1] flex items-center gap-2">
            <History size={15} className="text-[#0050c0]" />
            <h2 className="font-nunito text-sm font-extrabold text-[#383838]">Scoring history</h2>
          </div>
          {history.length === 0 ? (
            <p className="px-5 py-6 text-[13px] text-[#8a8a8a]">No scans yet — history appears here after the first score.</p>
          ) : (
            <div className="divide-y divide-[#f7f7f7]">
              {history.map((h) => (
                <div key={h.id} className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="font-nunito text-[13px] font-extrabold text-[#383838] truncate">{h.label}</p>
                    <p className="text-[11px] text-[#8a8a8a]">{new Date(h.created_at).toLocaleString()} · {h.flagged_count} flagged / {h.sentence_count} sentences</p>
                  </div>
                  <VerdictBadge cls={h.predicted_class} confidence={h.confidence_category} />
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
